import {
  TransactionLedgerRow,
  TransactionLedgerTotals,
  TransactionSortColumn,
  TransactionSortDirection
} from 'src/app/data/model/transaction-ledger';

/**
 * How a ledger row reads, shared by every screen that shows <app-transactions-table> (SD-1227:
 * the Transactions screen and the report coding view) so a row cannot mean one thing on one
 * and something else on the other.
 */

const NO_MATCH = '00000000-0000-0000-0000-000000000000';

/**
 * Done: reconciled in the accounting system, or matched to an invoice here. A relatedID is
 * a match the user has made (the panel shows it with Unmatch), not a suggestion, so it is
 * Reconciled rather than "Match found". Checked on the client as well as in
 * Upside_TransactionLedgerByAccount so the chip is right even before that migration runs.
 */
export function isReconciledRow(row: TransactionLedgerRow): boolean {
  return !!row.isReconciled || (!!row.relatedID && row.relatedID !== NO_MATCH);
}

export function formatMoney(value: number | null): string {
  return Math.abs(value || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2, maximumFractionDigits: 2
  });
}

export function formatSigned(value: number | null): string {
  return `${(value || 0) < 0 ? '−' : '+'}${formatMoney(value)}`;
}

/**
 * Sorts by `column`, or flips the direction if it is already the sorted one.
 *
 * Each column starts in the direction that is useful first: dates oldest-first, because
 * that is how the statement and its running balance read, and the others largest or
 * latest first, because that is what you open a column to find.
 */
export function nextSort(
  sortBy: TransactionSortColumn,
  sortDir: TransactionSortDirection,
  column: TransactionSortColumn
): { sortBy: TransactionSortColumn; sortDir: TransactionSortDirection } {
  if (sortBy === column) {
    return { sortBy, sortDir: sortDir === 'asc' ? 'desc' : 'asc' };
  }
  return { sortBy: column, sortDir: column === 'date' ? 'asc' : 'desc' };
}

export function emptyLedgerTotals(): TransactionLedgerTotals {
  return {
    openingBalance: 0, moneyIn: 0, moneyOut: 0, closingBalance: 0,
    periodCount: 0, toReconcileCount: 0, reconciledCount: 0
  };
}

/** Transaction ids are compared lower-cased: the API and the coding endpoint disagree on case. */
export function rowKey(id: string | null | undefined): string {
  return (id || '').toLowerCase();
}

/**
 * Where a row came from, from its ImportMethod, in the words the import history uses. The
 * stored values vary -- preview variants, a long-standing "ChromeExtenstion" misspelling,
 * "API" and "Api" -- so they are read loosely; anything unknown is shown as "Other".
 */
export function importSourceLabel(importMethod: string | null | undefined): string {
  const key = (importMethod || '').trim().toLowerCase();
  if (!key) { return ''; }
  if (key === 'stripefeed') { return 'Bank feed'; }
  if (key.startsWith('xero')) { return 'Xero'; }
  if (key.startsWith('spreadsheet')) { return 'Spreadsheet'; }
  if (key.startsWith('chromeextens')) { return 'Browser extension'; }
  if (key === 'api') { return 'API'; }
  return 'Other';
}
